import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface CanvasContextRequest {
  x: number;
  y: number;
  target: 'canvas' | 'node' | 'edge';
}

interface Props {
  request: CanvasContextRequest;
  selectedCount: number;
  selectedEdgeCount: number;
  selectedLocked: boolean;
  canUngroup: boolean;
  canBundleEdges: boolean;
  canUnbundleEdges: boolean;
  canPaste: boolean;
  canApplyStyle: boolean;
  canSmartDelete: boolean;
  onClose: () => void;
  onDuplicate: () => void;
  onInsertAfter: () => void;
  onSmartDelete: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  onFront: () => void;
  onBack: () => void;
  onGroup: () => void;
  onUngroup: () => void;
  onCopyStyle: () => void;
  onApplyStyle: () => void;
  onBundleEdges: () => void;
  onUnbundleEdges: () => void;
  onConnect: () => void;
  onPaste: () => void;
  onFit: () => void;
  onNewPage: () => void;
}

export function CanvasContextMenu(props: Props) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: Math.max(8, props.request.x), top: Math.max(8, props.request.y) });

  useLayoutEffect(() => {
    const element = menuRef.current;
    if (!element) return;
    const margin = 8;
    const rect = element.getBoundingClientRect();
    const left = Math.max(margin, Math.min(props.request.x, window.innerWidth - rect.width - margin));
    const top = Math.max(margin, Math.min(props.request.y, window.innerHeight - rect.height - margin));
    setPosition({ left, top });
  }, [props.request.x, props.request.y, props.request.target, props.selectedCount, props.selectedEdgeCount]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      const element = event.target as HTMLElement | null;
      if (!element?.closest('.canvas-context-menu')) props.onClose();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') props.onClose(); };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('pointerdown', close, true); window.removeEventListener('keydown', escape); };
  }, [props.onClose]);

  const style = position;
  const run = (action: () => void) => { action(); props.onClose(); };

  return (
    <div ref={menuRef} className="canvas-context-menu" role="menu" style={style} aria-label="Ações do diagrama">
      {props.request.target === 'canvas' ? (
        <>
          <button type="button" role="menuitem" disabled={!props.canPaste} onClick={() => run(props.onPaste)}>Colar <kbd>Ctrl V</kbd></button>
          <button type="button" role="menuitem" onClick={() => run(props.onFit)}>Ajustar ao conteúdo</button>
          <button type="button" role="menuitem" onClick={() => run(props.onNewPage)}>Nova página</button>
        </>
      ) : props.request.target === 'edge' ? (
        <>
          <div className="context-menu-label">{props.selectedEdgeCount > 1 ? `${props.selectedEdgeCount} conexões` : 'Conexão'}</div>
          <button type="button" role="menuitem" disabled={!props.canBundleEdges} onClick={() => run(props.onBundleEdges)}>Unir em tronco comum</button>
          {!props.canBundleEdges && <div className="context-menu-help">Selecione uma linha de uma ramificação com origem/destino em comum, ou selecione várias linhas com Shift + clique.</div>}
          {props.canBundleEdges && props.selectedEdgeCount === 1 && <div className="context-menu-help is-ready">Ramificação detectada automaticamente. O Draw unirá as linhas irmãs.</div>}
          <button type="button" role="menuitem" disabled={!props.canUnbundleEdges} onClick={() => run(props.onUnbundleEdges)}>Separar conexões</button>
          <div className="context-menu-separator" />
          <button type="button" role="menuitem" className="danger" onClick={() => run(props.onDelete)}>Excluir conexão <kbd>Del</kbd></button>
        </>
      ) : (
        <>
          <div className="context-menu-label">{props.selectedCount > 1 ? `${props.selectedCount} objetos` : 'Objeto'}</div>
          <button type="button" role="menuitem" onClick={() => run(props.onDuplicate)}>Duplicar <kbd>Ctrl D</kbd></button>
          <button type="button" role="menuitem" disabled={props.selectedCount !== 1} onClick={() => run(props.onInsertAfter)}>Adicionar próximo passo</button>
          <button type="button" role="menuitem" onClick={() => run(props.onConnect)} disabled={props.selectedCount < 1}>{props.selectedCount > 1 ? 'Conectar seleção' : 'Criar conexão'}</button>
          <button type="button" role="menuitem" disabled={props.selectedCount !== 1} onClick={() => run(props.onCopyStyle)}>Copiar estilo</button>
          <button type="button" role="menuitem" disabled={!props.canApplyStyle} onClick={() => run(props.onApplyStyle)}>Aplicar estilo copiado</button>
          <div className="context-menu-separator" />
          <button type="button" role="menuitem" onClick={() => run(props.onFront)}>Trazer para frente</button>
          <button type="button" role="menuitem" onClick={() => run(props.onBack)}>Enviar para trás</button>
          <button type="button" role="menuitem" onClick={() => run(props.onToggleLock)}>{props.selectedLocked ? 'Destravar' : 'Travar'}</button>
          <div className="context-menu-separator" />
          <button type="button" role="menuitem" disabled={props.selectedCount < 2} onClick={() => run(props.onGroup)}>Agrupar <kbd>Ctrl G</kbd></button>
          <button type="button" role="menuitem" disabled={!props.canUngroup} onClick={() => run(props.onUngroup)}>Desagrupar</button>
          <button type="button" role="menuitem" disabled={!props.canSmartDelete} onClick={() => run(props.onSmartDelete)}>Excluir e reconectar fluxo</button>
          <button type="button" role="menuitem" className="danger" onClick={() => run(props.onDelete)}>Excluir <kbd>Del</kbd></button>
        </>
      )}
    </div>
  );
}
